import React, { useEffect, useMemo, useState } from "react";
import { Users, Plus, Trash2, Pencil, Loader2, AlertTriangle, CheckCircle2, X } from "lucide-react";
import { adminApi } from "../lib/api";
import { useAuth } from "../lib/auth";

const ROLES = [
  { value: "superadmin", label: "Super-admin", desc: "Tous les droits (y compris paramètres)" },
  { value: "directeur", label: "Directeur", desc: "Tous les droits sauf paramètres" },
  { value: "chef_cellule", label: "Chef de cellule", desc: "Corpus + feedback + stats/logs" },
  { value: "agent", label: "Agent", desc: "Consultation du corpus + recherche" },
  { value: "moderateur", label: "Modérateur", desc: "Gestion des retours citoyens uniquement" },
  { value: "controleur", label: "Contrôleur", desc: "Lecture seule (audit)" },
];

const ROLE_COLOR = {
  superadmin: "bg-accent text-[#0D1B2A]",
  directeur: "bg-primary text-white",
  chef_cellule: "bg-primary/80 text-white",
  agent: "bg-primary/10 text-primary",
  moderateur: "bg-[#DBEAFE] text-[#1E40AF]",
  controleur: "bg-[#F3E8FF] text-[#6B21A8]",
};

export default function AdminUsers() {
  const { user: current } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | agent object
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    adminApi.listAgents()
      .then(({ data }) => setItems(data.items || []))
      .catch((e) => setError(e?.response?.data?.detail || "Erreur"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((a) =>
      [a.email, a.full_name, a.role, a.department, a.recovery_email]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(s))
    );
  }, [items, q]);

  const remove = async (agent) => {
    if (!window.confirm(`Supprimer définitivement ${agent.full_name} (${agent.email}) ?`)) return;
    try {
      await adminApi.deleteAgent(agent.id);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-2">/ utilisateurs & rôles</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-primary tracking-tight">Équipe administrative</h1>
          <p className="text-sm text-foreground/60 mt-2 max-w-2xl">
            Ajoutez des agents, modérateurs et contrôleurs avec des permissions granulaires.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing("new")}
          className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2.5 rounded-sm text-sm font-medium hover:bg-primary-light transition-colors"
          data-testid="users-new-btn"
        >
          <Plus size={14} /> Nouvel utilisateur
        </button>
      </div>

      {error && (
        <div className="mb-4 text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] px-3 py-2 rounded-sm">
          <AlertTriangle size={14} className="inline mr-1 -mt-0.5" />{error}
        </div>
      )}

      <div className="mb-4">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher par nom, email, rôle…"
          className="w-full md:w-96 bg-white border border-primary/20 rounded-sm px-3 py-2 text-sm outline-none focus:border-primary"
          data-testid="users-search-input"
        />
      </div>

      {loading ? (
        <div className="text-sm text-foreground/50">Chargement…</div>
      ) : (
        <div className="bg-white border border-primary/15 rounded-sm overflow-hidden" data-testid="users-table">
          <table className="w-full text-sm">
            <thead className="bg-primary/5 text-primary font-mono uppercase text-[10px] tracking-widest">
              <tr>
                <th className="text-left px-4 py-3">Nom</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Rôle</th>
                <th className="text-left px-4 py-3">Département</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-foreground/50">Aucun utilisateur.</td>
                </tr>
              )}
              {filtered.map((a) => (
                <tr key={a.id} className="border-t border-primary/10 hover:bg-primary/5">
                  <td className="px-4 py-3 font-medium text-foreground">
                    {a.full_name}
                    {a.email === current?.email && (
                      <span className="ml-2 font-mono text-[9px] uppercase tracking-wider bg-accent/20 text-[#b4551c] px-1.5 py-0.5 rounded-sm">vous</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-foreground/80 font-mono text-xs">
                    <div>{a.email}</div>
                    {a.recovery_email && <div className="text-foreground/40">↳ {a.recovery_email}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${ROLE_COLOR[a.role] || "bg-foreground/10"}`}>
                      {a.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-foreground/70 text-xs">{a.department || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => setEditing(a)}
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline mr-3"
                      data-testid={`users-edit-${a.id}`}
                    >
                      <Pencil size={12} /> Éditer
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(a)}
                      disabled={a.email === current?.email}
                      className="inline-flex items-center gap-1 text-xs text-[#991B1B] hover:underline disabled:opacity-30"
                      data-testid={`users-delete-${a.id}`}
                    >
                      <Trash2 size={12} /> Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <AgentModal
          agent={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          currentRole={current?.role}
        />
      )}
    </div>
  );
}

function AgentModal({ agent, onClose, onSaved, currentRole }) {
  const isNew = !agent;
  const [form, setForm] = useState({
    email: agent?.email || "",
    full_name: agent?.full_name || "",
    role: agent?.role || "agent",
    department: agent?.department || "",
    recovery_email: agent?.recovery_email || "",
    password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const availableRoles = ROLES.filter((r) => !(r.value === "superadmin" && currentRole !== "superadmin"));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSubmitting(true);
    try {
      if (isNew) {
        await adminApi.createAgent({
          email: form.email.trim().toLowerCase(),
          full_name: form.full_name.trim(),
          role: form.role,
          department: form.department || null,
          recovery_email: form.recovery_email || null,
          password: form.password,
        });
      } else {
        const patch = {
          full_name: form.full_name.trim(),
          role: form.role,
          department: form.department || null,
          recovery_email: form.recovery_email || null,
        };
        if (form.password && form.password.length >= 8) patch.password = form.password;
        await adminApi.updateAgent(agent.id, patch);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Échec");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0D1B2A]/70 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="user-modal"
    >
      <div className="relative w-full max-w-lg bg-white border border-primary/20 rounded-sm shadow-2xl p-6 md:p-8">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-foreground/50 hover:text-foreground"><X size={16} /></button>
        <h2 className="font-display text-2xl font-semibold text-primary mb-4">
          {isNew ? "Nouvel utilisateur" : "Modifier l'utilisateur"}
        </h2>
        {err && (
          <div className="mb-3 text-xs text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] px-3 py-2 rounded-sm" data-testid="user-modal-error">{err}</div>
        )}
        <form onSubmit={submit} className="space-y-3">
          <Row label="Email" required>
            <input
              type="email" required disabled={!isNew} value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-transparent text-sm py-2 outline-none disabled:opacity-60"
              data-testid="user-modal-email"
            />
          </Row>
          <Row label="Nom complet" required>
            <input
              type="text" required value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="w-full bg-transparent text-sm py-2 outline-none"
              data-testid="user-modal-fullname"
            />
          </Row>
          <Row label="Rôle" required>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full bg-transparent text-sm py-2 outline-none"
              data-testid="user-modal-role"
            >
              {availableRoles.map((r) => (
                <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>
              ))}
            </select>
          </Row>
          <Row label="Département (optionnel)">
            <input
              type="text" value={form.department || ""}
              onChange={(e) => setForm({ ...form, department: e.target.value })}
              className="w-full bg-transparent text-sm py-2 outline-none"
              data-testid="user-modal-dept"
            />
          </Row>
          <Row label="Email de récupération (pour reset MDP)">
            <input
              type="email" value={form.recovery_email || ""}
              onChange={(e) => setForm({ ...form, recovery_email: e.target.value })}
              placeholder="monautre@email.cm"
              className="w-full bg-transparent text-sm py-2 outline-none"
              data-testid="user-modal-recovery"
            />
          </Row>
          <Row label={isNew ? "Mot de passe (min 8 car.)" : "Nouveau mot de passe (laisser vide pour ne pas changer)"} required={isNew}>
            <input
              type="password" required={isNew} minLength={isNew ? 8 : undefined}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full bg-transparent text-sm py-2 outline-none font-mono"
              data-testid="user-modal-password"
            />
          </Row>

          <div className="flex items-center justify-end gap-2 pt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-foreground/70 hover:text-primary">Annuler</button>
            <button
              type="submit" disabled={submitting}
              className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-sm text-sm font-medium hover:bg-primary-light transition-colors disabled:opacity-60"
              data-testid="user-modal-save"
            >
              {submitting ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
              {isNew ? "Créer" : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Row({ label, required, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mb-1">
        {label}{required && <span className="text-[#E76F51]"> *</span>}
      </span>
      <div className="border border-primary/20 rounded-sm px-3 focus-within:border-primary transition-colors bg-white">{children}</div>
    </label>
  );
}
